<template>
  <div class="calendar-details">
    <div class="calendar-details__info">
      <StatusBanner
        v-if="isOverdue"
        type="negative"
        :title="$t`Вы на просрочке` + '!'"
        :subtitle="$t`Оплатите счет сегодня`"
        :amount="$currencyFormatter(data.to_pay)"
      />
      <StatusBanner
        v-else-if="data.status === 'PAID'"
        type="positive"
        icon="i-check-outline"
        :title="$t`Оплачен полностью`"
        :subtitle="`${$gettext('Дата оплаты')}: ${$getFormattedDate(
          data.paym_day_fact,
          'DD MMMM YYYY'
        )}`"
        :amount="$currencyFormatter(data.paid)"
      />
      <template v-else>
        <Divider />
        <StatusBanner
          :title="$t`К оплате`"
          :subtitle="
            data.status === 'FOR_PAYMENT'
              ? forpaymentDateText
              : prepaymentDateText
          "
          :amount="$currencyFormatter(data.to_pay)"
        />
        <Divider />
      </template>
    </div>
    <HistoryItems :items="historyItems" />
    <div class="spacer"></div>
    <CommonButton
      v-if="isOverdue || isForPayment"
      spaced
      class="calendar-details__footer"
      @click="onRepaymentClick"
    >
      <template v-if="isOverdue">
        {{ $t`Оплатить просроченный счет` }}
      </template>
      <template v-else>
        {{ $t`Оплатить счет` }}
      </template>
    </CommonButton>
    <CommonButton
      v-else-if="
        data.status === 'PRE_PAYMENT' &&
        data.get_detail === 2 &&
        data.to_pay > 0
      "
      spaced
      class="calendar-details__footer"
      @click="onPrepaymentClick"
    >
    <div>
      {{ $t`Погасить досрочно` }}
    </div>
   
    </CommonButton>
  </div>
</template>

<script>
import { getDateText } from "@/utils/date";
import StatusBanner from "@/components/bills-calendar/StatusBanner.vue";
import Divider from "@/components/shared/divider/Divider.vue";
import HistoryItems from "@/components/bills-calendar/CalendarHistoryItems.vue";
import CommonButton from "@/components/shared/button/CommonButton.vue";
import dayjs from "dayjs";
import { fetchPrepayments } from "@/api/bills";

export default {
  components: {
    Divider,
    HistoryItems,
    CommonButton,
    StatusBanner,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    hasAlert: {
      type: Boolean,
      required: true,
    },
    historyItems: {
      type: [Array, null],
      required: true,
    },
    currentPayment: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      payments: null,
    };
  },
  async mounted(){
    const payments = await fetchPrepayments();
    this.payments = payments;
  },
  computed: {
    prepaymentDateText() {
      if (!this.isValidDate(this.data.open_date)) {
        return "";
      }

      const text =
        this.data.to_pay === 0
          ? "Счет за новые покупки будет выставлен</br>#DATE#"
          : "Счет будет выставлен #DATE#";

      return this.$l10nformat(this.$gettext(text), {
        DATE: getDateText(this.data.open_date, "accusative"),
      });
    },
    forpaymentDateText() {
      if (!this.isValidDate(this.data.plan_date)) {
        return "";
      }
      return this.$l10nformat(this.$t`Оплатите счет до #DATE#`, {
        DATE: getDateText(this.data.plan_date, "dative"),
      });
    },
    isAlertShowable() {
      return this.hasAlert && this.data.status === "FOR_PAYMENT";
    },
    isBtnVisible() {
      return this.data && (this.isOverdue || this.isForPayment);
    },
    isOverdue() {
      return (
        this.data &&
        (this.data.status === "OVERDUE" || this.data.status === "WAS_OVERDUE")
      );
    },
    isForPayment() {
      return (
        this.data &&
        (this.data.status === "FOR_PAYMENT" || this.data.status === "UNPAID")
      );
    },
  },
  methods: {
    isValidDate(date) {
      return dayjs(date, "YYYY-MM-DD").isAfter("2016-01-01", "YEAR");
    },
    onPrepaymentClick() {
      if (this.currentPayment.plan_date !== this.data.plan_date) {
        this.$logAnalyticEvent("mybank_red_calendar_repayment_alert_seen");
        this.$store.dispatch("system/showAlert", {
          title: this.$l10nformat(
            this
              .$t`Чтобы досрочно оплатить счет за #ACTUAL#, оплатите счет за #CURRENT#`,
            {
              ACTUAL: getDateText(this.data.plan_date, "genitive", "MMMM"),
              CURRENT: getDateText(
                this.currentPayment.plan_date,
                "genitive",
                "MMMM"
              ),
            }
          ),
          btnLinkText: this.$l10nformat(this.$t`Оплатить счет за #DATE#`, {
            DATE: getDateText(
              this.currentPayment.plan_date,
              "genitive",
              "MMMM"
            ),
          }),
          btnLink: "/bank/red/redrepayment",
          btnCloseText: this.$t`Назад`,
        });
        return;
      }
      this.$router.push(`/bank/red/redprepayment?src=paymentcalendar&prepaymentAmount=${this.currentPayment.to_pay}`);
    },
    onRepaymentClick() {
      this.$logAnalyticEvent("mybank_red_calendar_bill_details_pay", {
        bill_type: "current",
      });

      if (!this.isAlertShowable) {
        this.$router.push("/bank/red/redrepayment");
        return;
      }

      this.$store.dispatch("system/showAlert", {
        title: this.$l10nformat(
          this
            .$t`Чтобы оплатить текущий счет за #DATE# необходимо сначала оплатить просроченный счет`,
          {
            DATE: this.$getFormattedDate(this.data.plan_date, "MMMM"),
          }
        ),
        btn: this.$t`Отменить`,
        btnLink: "/Bank/Red/RedRepayment",
        btnLinkText: this.$t`Оплатить просроченный счет`,
      });

      this.$logAnalyticEvent("mybank_red_calendar_bill_alert", {
        started_from: "mybank",
        name: "mybank_events",
        label:
          "Пользователь увидел окно Чтобы оплатить текущий счет, необходимо сначала оплатить просроченный счет",
      });
    },
  },

};
</script>

<style lang="scss" scoped>
.paid-head {
  display: flex;
  align-items: center;
  padding-bottom: 3px;

  &__icon {
    align-self: flex-start;
    margin-right: 12px;
  }

  &__content {
    flex: 1;

    &_title {
      margin-bottom: 4px;
      font-family: Roboto-Bold;
      font-size: 17px;
      line-height: 20px;
    }

    &_subtitle {
      font-size: 12px;
      line-height: 14px;
    }
  }

  &__amount {
    font-family: Roboto-Bold;
    font-size: 17px;
    line-height: 20px;
  }
}

.spacer {
  flex-grow: 1;
}

.calendar-details {
  position: relative;
  display: flex;
  justify-content: space-between;
  flex-direction: column;

  &__info {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: white;
  }

  &__wrapper {
    position: relative;
    padding-bottom: 34px;
    width: 100vw;
    overflow: hidden;
  }

  &__history {
    margin-bottom: -15px;
    overflow: scroll;
  }

  &__footer {
    position: -webkit-sticky;
    position: sticky;
    width: 100%;
    bottom: 0;
  }
}
</style>
